/**
 * Starting points for the handover instruction.
 *
 * The instruction is free text an agent follows literally, which is the right shape - Kruvi Access has no
 * business knowing what lives inside a customer's org - but it is a poor thing to ask an admin to write
 * from memory for the twentieth org this month. So the products we know about come with their wording
 * ready, and an admin edits it or replaces it entirely.
 *
 * Applying one is a copy, not a link: the text lands in the field and belongs to that org from then on.
 * An org running an older version, or one with its own conventions, keeps whatever it was changed to
 * rather than being quietly rewritten the next time this list changes.
 */
export interface HandoverTemplate {
  id: string;
  /** What the product is called, as an admin would recognise it. */
  name: string;
  /** Shown under the picker, so it is obvious whether this is the right one before applying it. */
  summary: string;
  text: string;
}

/**
 * Written against the actions Isimio AI ships today.
 *
 * Deliberately conservative: an instruction naming an action the installed version does not have fails
 * the call outright, and an agent meeting that failure goes back to exploring the org, which is the exact
 * behaviour the handover field exists to stop. So it names only what has been released, and the capability
 * line at the end is prose because there is not yet an action to ask.
 *
 * When a version ships that adds List Topics and actingFor, this wants updating to ask the org what it
 * covers rather than asserting it, and to pass actingFor so the conversation records who the work was for.
 */
const OMNI: HandoverTemplate = {
  id: 'omni',
  name: 'Isimio AI (Omni)',
  summary: 'Hands a task to Omni and reads the answer back. Edit the last line to say what your topics cover.',
  text:
    'To give Omni a task, POST to /services/data/v64.0/actions/custom/apex/kruvi_ai__InvocableAI with ' +
    'inputs: prompt (the task in plain English), notifyUrl (use callbackUrl), notifyKey (use ' +
    'callbackToken). It returns conversationId. Do not create Conversation__c records directly - nothing ' +
    'picks those up. When check_handover reports finished, read the outcome by calling ' +
    'kruvi_ai__InvocableConversationStatus with that conversationId. It will still say Active: Omni ' +
    'keeps a conversation open for two hours after the work is done so you can continue it by sending ' +
    'another prompt with the same conversationId. Active there means finished and still open to ' +
    'follow-up, not still working - do not wait for it to change. Omni can read and update Accounts.',
};

export const HANDOVER_TEMPLATES: HandoverTemplate[] = [OMNI];

/** The cap the field and the server both enforce, so a template can be checked against it at build time. */
export const MAX_HANDOVER = 900;
